import {z} from 'zod';

import {pick} from 'ramda';

import type {VariantTechnicalData} from '@dms/api/new-vehicle';

import type {Nullish} from 'shared/web';

import {
  optionalDecimalInput,
  optionalNonNegativeIntInput,
  optionalText,
} from '../utils/catalogueUi';
import {getTechnicalDataFit, type EngineFuel} from '../utils/getTechnicalDataFit';
import {presentTechnicalData} from '../utils/presentTechnicalData';

export const variantTechnicalDataSchema = z.object({
  electricRange: optionalNonNegativeIntInput,
  electricRangeCity: optionalNonNegativeIntInput,
  combinedRange: optionalNonNegativeIntInput,
  combinedRangeCity: optionalNonNegativeIntInput,
  fuelConsumptionWltpCombined: optionalDecimalInput,
  fuelConsumptionWltpCityCenter: optionalDecimalInput,
  fuelConsumptionWltpOutskirts: optionalDecimalInput,
  fuelConsumptionWltpCountryRoads: optionalDecimalInput,
  fuelConsumptionWltpHighway: optionalDecimalInput,
  fuelConsumptionWltpWeighted: optionalDecimalInput,
  fuelConsumptionWltpCombinedKwh: optionalDecimalInput,
  fuelConsumptionWltpCityCenterKwh: optionalDecimalInput,
  fuelConsumptionWltpOutskirtsKwh: optionalDecimalInput,
  fuelConsumptionWltpCountryRoadsKwh: optionalDecimalInput,
  fuelConsumptionWltpHighwayKwh: optionalDecimalInput,
  fuelConsumptionWltpWeightedCombinedKwh: optionalDecimalInput,
  carbonDioxideEmission: optionalDecimalInput,
  carbonDioxideEmissionDischarged: optionalDecimalInput,
  carbonDioxideEmissionWeightedCombined: optionalDecimalInput,
  emissionClass: optionalText,
  dimensions: z.object({
    length: optionalDecimalInput,
    width: optionalDecimalInput,
    height: optionalDecimalInput,
  }),
  wheelBase: optionalNonNegativeIntInput,
  operatingWeight: optionalNonNegativeIntInput,
  trunkSpace: optionalNonNegativeIntInput,
});
type VariantTechnicalDataValues = z.infer<typeof variantTechnicalDataSchema>;
type VariantTextKey = Exclude<keyof VariantTechnicalDataValues, 'dimensions'>;

export const toVariantTechnicalDataValues = (
  data: VariantTechnicalData | Nullish
): VariantTechnicalDataValues => {
  const text = (key: VariantTextKey) => data?.[key]?.toString() ?? '';
  return {
    electricRange: text('electricRange'),
    electricRangeCity: text('electricRangeCity'),
    combinedRange: text('combinedRange'),
    combinedRangeCity: text('combinedRangeCity'),
    fuelConsumptionWltpCombined: text('fuelConsumptionWltpCombined'),
    fuelConsumptionWltpCityCenter: text('fuelConsumptionWltpCityCenter'),
    fuelConsumptionWltpOutskirts: text('fuelConsumptionWltpOutskirts'),
    fuelConsumptionWltpCountryRoads: text('fuelConsumptionWltpCountryRoads'),
    fuelConsumptionWltpHighway: text('fuelConsumptionWltpHighway'),
    fuelConsumptionWltpWeighted: text('fuelConsumptionWltpWeighted'),
    fuelConsumptionWltpCombinedKwh: text('fuelConsumptionWltpCombinedKwh'),
    fuelConsumptionWltpCityCenterKwh: text('fuelConsumptionWltpCityCenterKwh'),
    fuelConsumptionWltpOutskirtsKwh: text('fuelConsumptionWltpOutskirtsKwh'),
    fuelConsumptionWltpCountryRoadsKwh: text('fuelConsumptionWltpCountryRoadsKwh'),
    fuelConsumptionWltpHighwayKwh: text('fuelConsumptionWltpHighwayKwh'),
    fuelConsumptionWltpWeightedCombinedKwh: text('fuelConsumptionWltpWeightedCombinedKwh'),
    carbonDioxideEmission: text('carbonDioxideEmission'),
    carbonDioxideEmissionDischarged: text('carbonDioxideEmissionDischarged'),
    carbonDioxideEmissionWeightedCombined: text('carbonDioxideEmissionWeightedCombined'),
    emissionClass: text('emissionClass'),
    dimensions: {
      length: data?.dimensions?.length ?? '',
      width: data?.dimensions?.width ?? '',
      height: data?.dimensions?.height ?? '',
    },
    wheelBase: text('wheelBase'),
    operatingWeight: text('operatingWeight'),
    trunkSpace: text('trunkSpace'),
  };
};

export const toVariantTechnicalData = (
  values: VariantTechnicalDataValues,
  engine: EngineFuel
): VariantTechnicalData => {
  const keys = getTechnicalDataFit(engine).variantKeys;
  const textKeys = keys.filter((key): key is VariantTextKey => key !== 'dimensions');
  const dimensions = Object.fromEntries(
    Object.entries(values.dimensions).filter(([, value]) => value !== '')
  );
  return {
    ...presentTechnicalData(variantTechnicalDataSchema.shape, pick(textKeys, values)),
    ...(keys.includes('dimensions') && Object.keys(dimensions).length ? {dimensions} : {}),
  };
};
